import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Review, ReviewFilter, SortOption } from '../types';
import { mensagemDeErro, reviewsService } from '../services';
import {
  Header,
  HeaderTop,
  HeaderTitle,
  Title,
  Subtitle,
  UpdateButton,
  TabsContainer,
  Tab,
  TabCount,
  FiltersBar,
  FilterButton,
  FilterIcon,
  SortSelect,
  ReviewsList,
  ReviewCard,
  ReviewHeader,
  ReviewAuthor,
  AuthorAvatar,
  AuthorInfo,
  AuthorName,
  RatingStars,
  ReviewDate,
  ReviewContent,
  ReviewText,
  ResponseSection,
  ResponseLabel,
  ResponseText,
  ResponseActions,
  ActionButton,
  TextArea,
  FiltersModal,
  FiltersModalOverlay,
  FiltersModalContent,
  FiltersModalHeader,
  FiltersModalTitle,
  CloseButton,
  FiltersModalBody,
  FilterGroup,
  FilterLabel,
  CheckboxGroup,
  CheckboxLabel,
  DateInput,
  FiltersModalFooter,
  ApplyButton,
  ClearFiltersButton,
} from '../pages/styles';

type TabType = 'all' | 'responded' | 'not-responded';

/** Quantas respostas dá para corrigir de uma vez: cada uma é uma chamada à IA e outra ao Google. */
export const LOTE_MAXIMO = 20;
const INSTRUCAO_MAXIMA = 1000;

type EstadoDaCorrecao = 'gerando' | 'pronta' | 'falhou' | 'publicada' | 'erro';

type Correcao = {
  review: Review;
  sugestao: string;
  texto: string;
  incluir: boolean;
  estado: EstadoDaCorrecao;
  erro?: string;
};

type Props = {
  profileId: string;
  canEdit: boolean;
  /** Dentro do editor do perfil: sem o título grande da página. */
  embedded?: boolean;
};

const caixa: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E6EA',
  borderRadius: 16,
  padding: 20,
};

const rotulo: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#2B2D31',
  marginBottom: 6,
};

const campo: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #E2E6EA',
  borderRadius: 12,
  padding: '10px 12px',
  font: 'inherit',
  fontSize: 14,
  color: '#1F1F1F',
  background: '#F7F9FC',
};

const ajuda: React.CSSProperties = {
  fontSize: 13,
  lineHeight: '18px',
  color: '#5F6368',
  margin: 0,
};

const selo = (fundo: string, cor: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: fundo,
  color: cor,
});

const dia = (iso: string) => iso.slice(0, 10);

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getAuthorInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

const renderStars = (rating: number) =>
  Array.from({ length: 5 }, (_, i) => (
    <span key={i} className="material-icons" aria-hidden="true">
      {i < rating ? 'star' : 'star_border'}
    </span>
  ));

export const ReviewsManager: React.FC<Props> = ({
  profileId,
  canEdit,
  embedded = false,
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [filters, setFilters] = useState<ReviewFilter>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('most-recent');

  // Resposta em edição: nova (responder) ou correção de uma já publicada.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [correctionNote, setCorrectionNote] = useState('');
  const [lastAiText, setLastAiText] = useState<string | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [saving, setSaving] = useState(false);

  // Como responder às próximas.
  const [instrOpen, setInstrOpen] = useState(false);
  const [instrText, setInstrText] = useState('');
  const [instrWords, setInstrWords] = useState('');
  const [instrSaved, setInstrSaved] = useState<string | null>(null);
  const [savingInstr, setSavingInstr] = useState(false);

  // Correção em lote.
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStep, setBulkStep] = useState<'motivo' | 'revisao'>('motivo');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkItems, setBulkItems] = useState<Correcao[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setReviews(await reviewsService.list(profileId));
    } catch (error) {
      setLoadError(
        mensagemDeErro(error, 'Não foi possível carregar as avaliações.'),
      );
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    setSelected([]);
    setEditingId(null);
    load();
  }, [load]);

  useEffect(() => {
    if (!profileId) return;
    let ativo = true;
    reviewsService
      .getInstructions(profileId)
      .then((atual) => {
        if (!ativo) return;
        setInstrText(atual.instructions ?? '');
        setInstrWords(atual.preferredWords.join(', '));
        setInstrSaved(atual.instructions);
      })
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, [profileId]);

  const tabCounts = useMemo(() => {
    const total = reviews.length;
    const responded = reviews.filter((r) => r.response).length;
    return { total, responded, notResponded: total - responded };
  }, [reviews]);

  const filteredAndSortedReviews = useMemo(() => {
    let filtered = reviews.filter((r) =>
      activeTab === 'responded'
        ? !!r.response
        : activeTab === 'not-responded'
          ? !r.response
          : true,
    );
    if (filters.rating && filters.rating.length > 0) {
      filtered = filtered.filter((r) => filters.rating!.includes(r.rating));
    }
    if (filters.startDate) {
      filtered = filtered.filter((r) => dia(r.date) >= filters.startDate!);
    }
    if (filters.endDate) {
      filtered = filtered.filter((r) => dia(r.date) <= filters.endDate!);
    }
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'highest-rating':
          return b.rating - a.rating;
        case 'lowest-rating':
          return a.rating - b.rating;
        default:
          return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
    });
  }, [reviews, activeTab, filters, sortBy]);

  const handleRatingFilterChange = (rating: number, checked: boolean) => {
    setFilters((prev) => {
      const currentRatings = prev.rating || [];
      const newRatings = checked
        ? [...currentRatings, rating]
        : currentRatings.filter((r) => r !== rating);
      return { ...prev, rating: newRatings.length > 0 ? newRatings : undefined };
    });
  };

  const fecharEditor = () => {
    setEditingId(null);
    setResponseText('');
    setCorrectionNote('');
    setLastAiText(null);
  };

  const abrirEditor = (review: Review) => {
    // Responder: parte do rascunho da IA, se houver. Corrigir: parte do que está no Google.
    const inicial = review.response?.text ?? review.draft ?? '';
    setEditingId(review.id);
    setResponseText(inicial);
    setCorrectionNote('');
    setLastAiText(review.response ? null : (review.draft ?? null));
  };

  const gerarComIA = async (review: Review) => {
    setGeneratingAI(true);
    try {
      const sugestao = await reviewsService.suggestReply(
        review.id,
        review.response ? correctionNote : undefined,
      );
      if (!sugestao.trim()) {
        toast.info('A IA não devolveu texto. Tente de novo.');
        return;
      }
      setResponseText(sugestao);
      setLastAiText(sugestao);
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível gerar a resposta.'));
    } finally {
      setGeneratingAI(false);
    }
  };

  const aplicarNaLista = (
    reviewId: string,
    texto: string,
    source: 'AI' | 'HUMAN',
    editada: boolean,
  ) => {
    setReviews((prev) =>
      prev.map((review) =>
        review.id === reviewId
          ? {
              ...review,
              status: 'REPLIED',
              draft: undefined,
              errorMsg: undefined,
              response: {
                id: review.id,
                text: texto,
                date: new Date().toISOString(),
                isEdited: editada,
                source,
              },
            }
          : review,
      ),
    );
  };

  const publicar = async (review: Review) => {
    const texto = responseText.trim();
    if (!texto) {
      toast.error('Escreva a resposta antes de publicar.');
      return;
    }
    const source = lastAiText && lastAiText.trim() === texto ? 'AI' : 'HUMAN';
    setSaving(true);
    try {
      await reviewsService.saveReply(review.id, texto, source);
      aplicarNaLista(review.id, texto, source, !!review.response);
      toast.success(
        review.response
          ? 'Resposta corrigida no Google.'
          : 'Resposta publicada no Google.',
      );
      fecharEditor();
    } catch (error) {
      toast.error(
        mensagemDeErro(error, 'Não foi possível publicar a resposta no Google.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const apagar = async (review: Review) => {
    if (!window.confirm('Apagar esta resposta no Google?')) return;
    try {
      await reviewsService.deleteReply(review.id);
      setReviews((prev) =>
        prev.map((r) =>
          r.id === review.id
            ? { ...r, status: 'WAITING_REPLY', response: undefined }
            : r,
        ),
      );
      setSelected((prev) => prev.filter((id) => id !== review.id));
      toast.success('Resposta apagada no Google.');
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível apagar a resposta.'));
    }
  };

  const salvarInstrucao = async () => {
    setSavingInstr(true);
    try {
      const salvo = await reviewsService.saveInstructions(profileId, {
        instructions: instrText.trim(),
        preferredWords: instrWords
          .split(',')
          .map((palavra) => palavra.trim())
          .filter(Boolean),
      });
      setInstrText(salvo.instructions ?? '');
      setInstrWords(salvo.preferredWords.join(', '));
      setInstrSaved(salvo.instructions);
      toast.success('Instrução salva. Vale para as próximas respostas.');
    } catch (error) {
      toast.error(mensagemDeErro(error, 'Não foi possível salvar a instrução.'));
    } finally {
      setSavingInstr(false);
    }
  };

  const alternarSelecao = (reviewId: string) => {
    setSelected((prev) => {
      if (prev.includes(reviewId)) return prev.filter((id) => id !== reviewId);
      if (prev.length >= LOTE_MAXIMO) {
        toast.info(`Corrija até ${LOTE_MAXIMO} respostas por vez.`);
        return prev;
      }
      return [...prev, reviewId];
    });
  };

  const abrirLote = () => {
    setBulkItems(
      reviews
        .filter((r) => selected.includes(r.id) && r.response)
        .map((review) => ({
          review,
          sugestao: '',
          texto: '',
          incluir: true,
          estado: 'gerando' as const,
        })),
    );
    setBulkNote('');
    setBulkStep('motivo');
    setBulkOpen(true);
  };

  const atualizarItem = (reviewId: string, mudanca: Partial<Correcao>) =>
    setBulkItems((prev) =>
      prev.map((item) =>
        item.review.id === reviewId ? { ...item, ...mudanca } : item,
      ),
    );

  const gerarLote = async () => {
    if (!bulkNote.trim()) {
      toast.error('Descreva o que estava errado nas respostas.');
      return;
    }
    setBulkStep('revisao');
    setBulkBusy(true);
    // Uma por vez: a IA é chamada em série para não estourar o limite de requisições.
    for (const item of bulkItems) {
      atualizarItem(item.review.id, { estado: 'gerando', erro: undefined });
      try {
        const sugestao = await reviewsService.suggestReply(
          item.review.id,
          bulkNote,
        );
        atualizarItem(
          item.review.id,
          sugestao.trim()
            ? { estado: 'pronta', sugestao, texto: sugestao, incluir: true }
            : { estado: 'falhou', incluir: false, erro: 'A IA não devolveu texto.' },
        );
      } catch (error) {
        atualizarItem(item.review.id, {
          estado: 'falhou',
          incluir: false,
          erro: mensagemDeErro(error, 'Falha ao gerar.'),
        });
      }
    }
    setBulkBusy(false);
  };

  const publicarLote = async () => {
    const paraPublicar = bulkItems.filter(
      (item) => item.incluir && item.estado === 'pronta' && item.texto.trim(),
    );
    if (paraPublicar.length === 0) {
      toast.info('Nenhuma correção marcada para publicar.');
      return;
    }
    setBulkBusy(true);
    let publicadas = 0;
    for (const item of paraPublicar) {
      const texto = item.texto.trim();
      const source = texto === item.sugestao.trim() ? 'AI' : 'HUMAN';
      try {
        await reviewsService.saveReply(item.review.id, texto, source);
        aplicarNaLista(item.review.id, texto, source, true);
        atualizarItem(item.review.id, { estado: 'publicada' });
        publicadas += 1;
      } catch (error) {
        atualizarItem(item.review.id, {
          estado: 'erro',
          erro: mensagemDeErro(error, 'Falha ao publicar no Google.'),
        });
      }
    }
    setBulkBusy(false);
    setSelected([]);
    const falhas = paraPublicar.length - publicadas;
    if (falhas === 0) {
      toast.success(`${publicadas} resposta(s) corrigida(s) no Google.`);
    } else {
      toast.warning(
        `${publicadas} corrigida(s), ${falhas} com falha. Veja o motivo em cada uma.`,
      );
    }
  };

  const fecharLote = () => {
    if (bulkBusy) return;
    setBulkOpen(false);
    setBulkItems([]);
  };

  const prontas = bulkItems.filter(
    (item) => item.incluir && item.estado === 'pronta',
  ).length;
  const loteTerminou =
    bulkItems.length > 0 &&
    bulkItems.some((item) => item.estado === 'publicada' || item.estado === 'erro');
  const activeFilters =
    (filters.rating?.length || 0) +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0);

  const renderEditor = (review: Review) => {
    const corrigindo = !!review.response;
    return (
      <ResponseSection>
        <ResponseLabel>
          <span className="material-icons" aria-hidden="true">edit</span>
          {corrigindo ? 'Corrigir resposta' : 'Escrever resposta'}
        </ResponseLabel>
        {corrigindo && (
          <div style={{ marginBottom: 12 }}>
            <label style={rotulo} htmlFor={`erro-${review.id}`}>
              O que estava errado? (opcional, orienta a IA ao refazer)
            </label>
            <input
              id={`erro-${review.id}`}
              style={campo}
              value={correctionNote}
              maxLength={500}
              onChange={(e) => setCorrectionNote(e.target.value)}
              placeholder="Ex.: a resposta disse que abrimos aos domingos, e não abrimos"
              disabled={generatingAI || saving}
            />
          </div>
        )}
        <TextArea
          value={responseText}
          onChange={(e) => setResponseText(e.target.value)}
          placeholder="Digite a resposta ao comentário..."
          aria-label="Texto da resposta"
          disabled={generatingAI || saving}
        />
        <ResponseActions>
          <ActionButton
            variant="ai"
            onClick={() => gerarComIA(review)}
            disabled={generatingAI || saving}
            type="button"
          >
            <span className="material-icons" aria-hidden="true">auto_awesome</span>
            {generatingAI
              ? 'Gerando...'
              : corrigindo
                ? 'Refazer com IA'
                : 'Gerar resposta'}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={() => publicar(review)}
            disabled={generatingAI || saving}
            type="button"
          >
            <span className="material-icons" aria-hidden="true">send</span>
            {saving
              ? 'Publicando...'
              : corrigindo
                ? 'Publicar correção'
                : 'Publicar resposta'}
          </ActionButton>
          <ActionButton onClick={fecharEditor} disabled={saving} type="button">
            <span className="material-icons" aria-hidden="true">close</span>
            Cancelar
          </ActionButton>
        </ResponseActions>
      </ResponseSection>
    );
  };

  return (
    <>
      <Header>
        <HeaderTop>
          <HeaderTitle>
            {!embedded && <Title>Avaliações</Title>}
            <Subtitle>
              Responda às avaliações do perfil, corrija respostas já publicadas e
              diga como a IA deve responder as próximas.
            </Subtitle>
          </HeaderTitle>
          <UpdateButton onClick={load} disabled={loading} type="button">
            <span className="material-icons" aria-hidden="true">refresh</span>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </UpdateButton>
        </HeaderTop>

        <TabsContainer>
          <Tab $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
            Todas
            <TabCount>{tabCounts.total}</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'responded'}
            onClick={() => setActiveTab('responded')}
          >
            Respondidas
            <TabCount>{tabCounts.responded}</TabCount>
          </Tab>
          <Tab
            $active={activeTab === 'not-responded'}
            onClick={() => setActiveTab('not-responded')}
          >
            Não respondidas
            <TabCount>{tabCounts.notResponded}</TabCount>
          </Tab>
        </TabsContainer>
      </Header>

      <div style={{ padding: '16px 32px 0' }}>
        <section style={caixa} aria-labelledby="como-responder">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <strong id="como-responder" style={{ fontSize: 15, color: '#1F1F1F' }}>
                Como responder as próximas avaliações
              </strong>
              <p style={ajuda}>
                {instrSaved
                  ? 'A IA segue a instrução abaixo nas respostas automáticas e nas sugestões.'
                  : 'Sem instrução: a IA responde no tom padrão (agradece, é cordial e cita o negócio).'}
              </p>
            </div>
            <ActionButton
              type="button"
              onClick={() => setInstrOpen((aberto) => !aberto)}
              aria-expanded={instrOpen}
            >
              <span className="material-icons" aria-hidden="true">
                {instrOpen ? 'expand_less' : 'tune'}
              </span>
              {instrOpen ? 'Fechar' : instrSaved ? 'Ver ou alterar' : 'Definir instrução'}
            </ActionButton>
          </div>

          {instrOpen && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={rotulo} htmlFor="instrucao-resposta">
                  Instrução para a IA
                </label>
                <TextArea
                  id="instrucao-resposta"
                  value={instrText}
                  maxLength={INSTRUCAO_MAXIMA}
                  onChange={(e) => setInstrText(e.target.value)}
                  placeholder="Ex.: usar tom formal; agradecer sempre pelo nome; nunca prometer prazo de entrega; em avaliação negativa, pedir que o cliente nos chame por mensagem."
                  disabled={!canEdit || savingInstr}
                />
                <p style={ajuda}>
                  {instrText.length}/{INSTRUCAO_MAXIMA}. A IA não inclui telefone,
                  WhatsApp nem link nas respostas, mesmo que a instrução peça.
                </p>
              </div>
              <div>
                <label style={rotulo} htmlFor="palavras-resposta">
                  Palavras para usar quando couber (separadas por vírgula)
                </label>
                <input
                  id="palavras-resposta"
                  style={campo}
                  value={instrWords}
                  onChange={(e) => setInstrWords(e.target.value)}
                  placeholder="Ex.: pontualidade, garantia, equipe especializada"
                  disabled={!canEdit || savingInstr}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {canEdit && (
                  <ActionButton
                    variant="primary"
                    type="button"
                    onClick={salvarInstrucao}
                    disabled={savingInstr}
                  >
                    <span className="material-icons" aria-hidden="true">save</span>
                    {savingInstr ? 'Salvando...' : 'Salvar instrução'}
                  </ActionButton>
                )}
                <p style={ajuda}>
                  Vale para as próximas respostas. Para consertar respostas já
                  publicadas, use “Corrigir” em cada uma ou selecione várias.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>

      <FiltersBar>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterButton onClick={() => setIsFilterModalOpen(true)}>
            <FilterIcon className="material-icons">filter_list</FilterIcon>
            Filtros
            {activeFilters > 0 && <TabCount color="#1a73e8">{activeFilters}</TabCount>}
          </FilterButton>
          {canEdit && selected.length > 0 && (
            <>
              <ActionButton variant="primary" type="button" onClick={abrirLote}>
                <span className="material-icons" aria-hidden="true">auto_fix_high</span>
                Corrigir {selected.length} resposta(s)
              </ActionButton>
              <ActionButton type="button" onClick={() => setSelected([])}>
                Limpar seleção
              </ActionButton>
            </>
          )}
        </div>

        <SortSelect
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          aria-label="Ordenar avaliações"
        >
          <option value="most-recent">Mais recentes</option>
          <option value="oldest">Mais antigas</option>
          <option value="highest-rating">Maior nota</option>
          <option value="lowest-rating">Menor nota</option>
        </SortSelect>
      </FiltersBar>

      <ReviewsList>
        {loading && reviews.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#5F6368' }} role="status">
            Carregando avaliações...
          </div>
        ) : loadError ? (
          <div style={{ ...caixa, textAlign: 'center' }} role="alert">
            <h3 style={{ fontSize: 16, margin: '0 0 8px', color: '#1F1F1F' }}>
              Não foi possível carregar as avaliações
            </h3>
            <p style={{ ...ajuda, marginBottom: 12 }}>{loadError}</p>
            <ActionButton variant="primary" type="button" onClick={load}>
              <span className="material-icons" aria-hidden="true">refresh</span>
              Tentar de novo
            </ActionButton>
          </div>
        ) : filteredAndSortedReviews.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#5f6368' }}>
            <span
              className="material-icons"
              style={{ fontSize: '64px', color: '#dadce0', marginBottom: '16px' }}
            >
              inbox
            </span>
            <h3 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
              {reviews.length === 0
                ? 'Este perfil ainda não tem avaliações importadas'
                : 'Nenhuma avaliação com esses filtros'}
            </h3>
            <p style={{ fontSize: '14px' }}>
              {reviews.length === 0
                ? 'As avaliações do Google entram aqui na sincronização diária.'
                : 'Ajuste os filtros para ver as demais.'}
            </p>
          </div>
        ) : (
          filteredAndSortedReviews.map((review) => (
            <ReviewCard key={review.id}>
              <ReviewHeader>
                <ReviewAuthor>
                  {canEdit && review.response && (
                    <input
                      type="checkbox"
                      checked={selected.includes(review.id)}
                      onChange={() => alternarSelecao(review.id)}
                      aria-label={`Selecionar a resposta para ${review.authorName} para corrigir em lote`}
                      style={{ width: 18, height: 18, marginRight: 4 }}
                    />
                  )}
                  <AuthorAvatar>{getAuthorInitials(review.authorName)}</AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{review.authorName}</AuthorName>
                    <RatingStars aria-label={`${review.rating} de 5 estrelas`}>
                      {renderStars(review.rating)}
                    </RatingStars>
                  </AuthorInfo>
                </ReviewAuthor>
                <ReviewDate>{formatDate(review.date)}</ReviewDate>
              </ReviewHeader>

              <ReviewContent>
                <ReviewText>
                  {review.comment || 'Avaliação só com nota, sem comentário.'}
                </ReviewText>
              </ReviewContent>

              {!review.response && editingId !== review.id && (
                <>
                  {review.status === 'ERROR' && (
                    <p style={{ ...ajuda, color: '#B3261E', marginBottom: 8 }} role="alert">
                      A última tentativa de responder no Google falhou
                      {review.errorMsg ? `: ${review.errorMsg}` : '.'}
                    </p>
                  )}
                  {review.draft && review.status !== 'ERROR' && (
                    <p style={{ ...ajuda, marginBottom: 8 }}>
                      <span style={selo('#EAF1FB', '#0B4FA8')}>Rascunho da IA</span>{' '}
                      aguardando o envio automático. Você pode revisar e publicar agora.
                    </p>
                  )}
                  {canEdit && (
                    <ResponseActions>
                      <ActionButton
                        variant="primary"
                        type="button"
                        onClick={() => abrirEditor(review)}
                      >
                        <span className="material-icons" aria-hidden="true">reply</span>
                        {review.draft ? 'Revisar e responder' : 'Responder'}
                      </ActionButton>
                    </ResponseActions>
                  )}
                </>
              )}

              {review.response && editingId !== review.id && (
                <ResponseSection>
                  <ResponseLabel>
                    <span className="material-icons" aria-hidden="true">
                      {review.response.source === 'AI' ? 'auto_awesome' : 'business'}
                    </span>
                    {review.response.source === 'AI'
                      ? 'Resposta publicada pela IA'
                      : 'Resposta publicada'}
                    {review.response.isEdited && ' (corrigida agora)'}
                  </ResponseLabel>
                  <ResponseText>{review.response.text}</ResponseText>
                  {canEdit && (
                    <ResponseActions>
                      <ActionButton type="button" onClick={() => abrirEditor(review)}>
                        <span className="material-icons" aria-hidden="true">edit</span>
                        Corrigir
                      </ActionButton>
                      <ActionButton
                        variant="danger"
                        type="button"
                        onClick={() => apagar(review)}
                      >
                        <span className="material-icons" aria-hidden="true">delete</span>
                        Apagar
                      </ActionButton>
                    </ResponseActions>
                  )}
                </ResponseSection>
              )}

              {editingId === review.id && renderEditor(review)}
            </ReviewCard>
          ))
        )}
      </ReviewsList>

      {isFilterModalOpen && (
        <FiltersModal $isOpen={isFilterModalOpen}>
          <FiltersModalOverlay onClick={() => setIsFilterModalOpen(false)}>
            <FiltersModalContent onClick={(e) => e.stopPropagation()}>
              <FiltersModalHeader>
                <FiltersModalTitle>Filtros</FiltersModalTitle>
                <CloseButton
                  onClick={() => setIsFilterModalOpen(false)}
                  aria-label="Fechar filtros"
                >
                  <span className="material-icons" aria-hidden="true">close</span>
                </CloseButton>
              </FiltersModalHeader>

              <FiltersModalBody>
                <FilterGroup>
                  <FilterLabel>Nota</FilterLabel>
                  <CheckboxGroup>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <CheckboxLabel key={rating}>
                        <input
                          type="checkbox"
                          checked={filters.rating?.includes(rating) || false}
                          onChange={(e) =>
                            handleRatingFilterChange(rating, e.target.checked)
                          }
                          aria-label={`${rating} estrela(s)`}
                        />
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {Array.from({ length: rating }, (_, i) => (
                            <span
                              key={i}
                              className="material-icons"
                              style={{ fontSize: '16px', color: '#fbbc04' }}
                              aria-hidden="true"
                            >
                              star
                            </span>
                          ))}
                        </div>
                      </CheckboxLabel>
                    ))}
                  </CheckboxGroup>
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Data inicial</FilterLabel>
                  <DateInput
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                    }
                  />
                </FilterGroup>

                <FilterGroup>
                  <FilterLabel>Data final</FilterLabel>
                  <DateInput
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                    }
                  />
                </FilterGroup>
              </FiltersModalBody>

              <FiltersModalFooter>
                <ClearFiltersButton onClick={() => setFilters({})}>
                  Limpar filtros
                </ClearFiltersButton>
                <ApplyButton onClick={() => setIsFilterModalOpen(false)}>
                  Aplicar
                </ApplyButton>
              </FiltersModalFooter>
            </FiltersModalContent>
          </FiltersModalOverlay>
        </FiltersModal>
      )}

      {bulkOpen && (
        <FiltersModal $isOpen={bulkOpen}>
          <FiltersModalOverlay onClick={fecharLote}>
            <FiltersModalContent
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 760, width: '94vw' }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="titulo-lote"
            >
              <FiltersModalHeader>
                <FiltersModalTitle id="titulo-lote">
                  Corrigir {bulkItems.length} resposta(s)
                </FiltersModalTitle>
                <CloseButton onClick={fecharLote} aria-label="Fechar" disabled={bulkBusy}>
                  <span className="material-icons" aria-hidden="true">close</span>
                </CloseButton>
              </FiltersModalHeader>

              <FiltersModalBody style={{ maxHeight: '64vh', overflowY: 'auto' }}>
                {bulkStep === 'motivo' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={rotulo} htmlFor="motivo-lote">
                      O que estava errado nessas respostas?
                    </label>
                    <TextArea
                      id="motivo-lote"
                      value={bulkNote}
                      maxLength={500}
                      onChange={(e) => setBulkNote(e.target.value)}
                      placeholder="Ex.: as respostas citaram entrega grátis, e nós não fazemos entrega"
                    />
                    <p style={ajuda}>
                      A IA refaz cada resposta corrigindo esse erro e seguindo a
                      instrução do perfil. Nada vai para o Google antes de você
                      revisar a lista.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {bulkItems.map((item) => (
                      <div key={item.review.id} style={{ ...caixa, padding: 14 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            marginBottom: 8,
                          }}
                        >
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.incluir}
                              disabled={item.estado !== 'pronta' || bulkBusy}
                              onChange={(e) =>
                                atualizarItem(item.review.id, {
                                  incluir: e.target.checked,
                                })
                              }
                              style={{ width: 18, height: 18 }}
                            />
                            {item.review.authorName} · {item.review.rating} estrela(s)
                          </label>
                          {item.estado === 'gerando' && (
                            <span style={selo('#EAF1FB', '#0B4FA8')}>Gerando...</span>
                          )}
                          {item.estado === 'falhou' && (
                            <span style={selo('#FCE8E6', '#B3261E')}>Não gerou</span>
                          )}
                          {item.estado === 'publicada' && (
                            <span style={selo('#EAF7EE', '#137333')}>Publicada</span>
                          )}
                          {item.estado === 'erro' && (
                            <span style={selo('#FCE8E6', '#B3261E')}>Falhou ao publicar</span>
                          )}
                        </div>
                        {item.review.comment && (
                          <p style={{ ...ajuda, marginBottom: 8 }}>
                            Avaliação: {item.review.comment}
                          </p>
                        )}
                        <p style={{ ...ajuda, marginBottom: 8 }}>
                          Resposta atual: {item.review.response?.text}
                        </p>
                        {(item.estado === 'pronta' ||
                          item.estado === 'publicada' ||
                          item.estado === 'erro') && (
                          <TextArea
                            value={item.texto}
                            onChange={(e) =>
                              atualizarItem(item.review.id, { texto: e.target.value })
                            }
                            aria-label={`Nova resposta para ${item.review.authorName}`}
                            disabled={item.estado !== 'pronta' || bulkBusy}
                          />
                        )}
                        {item.erro && (
                          <p style={{ ...ajuda, color: '#B3261E', marginTop: 6 }} role="alert">
                            {item.erro}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </FiltersModalBody>

              <FiltersModalFooter>
                <ClearFiltersButton onClick={fecharLote} disabled={bulkBusy}>
                  {loteTerminou ? 'Fechar' : 'Cancelar'}
                </ClearFiltersButton>
                {bulkStep === 'motivo' ? (
                  <ApplyButton onClick={gerarLote}>Gerar novas respostas</ApplyButton>
                ) : (
                  !loteTerminou && (
                    <ApplyButton onClick={publicarLote} disabled={bulkBusy || prontas === 0}>
                      {bulkBusy ? 'Aguarde...' : `Publicar ${prontas} correção(ões)`}
                    </ApplyButton>
                  )
                )}
              </FiltersModalFooter>
            </FiltersModalContent>
          </FiltersModalOverlay>
        </FiltersModal>
      )}
    </>
  );
};

export default ReviewsManager;
